/**
 * Konto löschen (#274, seit 15.10.2026). AGB § 12 nennt eine Löschfunktion im
 * Konto; vorher ging es nur per Mail. Der Server (`billing/konto-loeschen`,
 * `konto_loeschen.ts`) kündigt ein laufendes Abo zuerst und löscht dann
 * Konto, Anmeldung und Bildschirmfotos, wie `scripts/konto-loeschen.mjs`.
 *
 * Zugeklappt, und zur Bestätigung die eigene E-Mail-Adresse: ein Knopf allein
 * wäre zu leicht zu treffen, und rückgängig machen lässt sich nichts.
 */

import { useState, type FormEvent } from 'react';
import { ApiFehler, loescheKonto, type NewsletterStand } from '../api';
import { useSitzung } from '../session';
import { Hinweis, KleinerKnopf, Panel } from '../ui';
import { t } from '../sprache';

export default function KontoLoeschen({ email, abo, newsletter }: { email: string; abo: boolean; newsletter?: NewsletterStand }) {
  const { abmelden } = useSitzung();
  const [offen, setOffen] = useState(false);
  const [eingabe, setEingabe] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  // Das Newsletter-Abo hängt nicht am Konto (newsletter.ts). Wer es auch beenden will, hakt es hier an.
  const [auchNewsletter, setAuchNewsletter] = useState(false);
  const mitNewsletter = newsletter === 'an' || newsletter === 'offen';
  const passt = !!email && eingabe.trim().toLowerCase() === email.trim().toLowerCase();

  async function absenden(e: FormEvent) {
    e.preventDefault();
    if (!passt) return;
    setFehler('');
    setLaedt(true);
    try {
      await loescheKonto(eingabe.trim(), mitNewsletter && auchNewsletter);
      await abmelden().catch(() => {});
      // Fester Seitenwechsel statt navigate: nach dem Abmelden leitet der
      // Seitenschutz von /account sofort auf /login?next=… um, und die
      // Komponente ist weg, bevor ihr navigate greift.
      window.location.replace('/login?geloescht=1');
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : t('Das hat nicht geklappt. Versuch es noch einmal.', 'That did not work. Please try again.'));
      setLaedt(false);
    }
  }

  return (
    <Panel>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5 px-4 py-3.5 sm:px-5">
        <span className="min-w-0">
          <span className="block text-[0.9375rem] font-semibold text-tinte">{t('Konto löschen', 'Delete account')}</span>
          <span className="mt-0.5 block text-[0.8125rem] text-text-hell">{t('Löscht dein Konto mit allem, was darin liegt. Rückgängig machen lässt sich das nicht.', 'Deletes your account with everything in it. This cannot be undone.')}</span>
        </span>
        {!offen && (
          <KleinerKnopf type="button" onClick={() => setOffen(true)} className="hover:!border-schlecht hover:!text-schlecht">
            {t('Konto löschen …', 'Delete account …')}
          </KleinerKnopf>
        )}
      </div>
      {offen && (
        <form onSubmit={absenden} className="border-t border-linie-zart px-4 py-4 sm:px-5">
          <p className="m-0 text-[0.875rem] leading-relaxed text-text">{t('Damit löschen wir:', 'This deletes:')}</p>
          <ul className="mt-1.5 mb-3 list-disc pl-5 text-[0.875rem] leading-relaxed text-text">
            <li>{t('dein Konto, deine Anmeldung und deine API-Schlüssel,', 'your account, your login and your API keys,')}</li>
            <li>{t('deine eigenen Zielgruppen, Runs und Berichte; veröffentlichte Berichtslinks funktionieren danach nicht mehr,', 'your own audiences, runs and reports; published report links stop working,')}</li>
            <li>{t('die Bildschirmfotos aus Design-Checks und Seitentests,', 'the screenshots from design checks and page tests,')}</li>
            <li>{t('dein Guthaben: ungenutzte Credits verfallen.', 'your balance: unused credits expire.')}</li>
          </ul>
          <p className="m-0 mb-3 text-[0.8125rem] leading-relaxed text-text-hell">
            {abo && t('Dein laufendes Abo kündigen wir vorher, es wird nichts mehr abgebucht. ', 'We cancel your running subscription first, nothing more will be charged. ')}
            {t('Rechnungen bewahren wir auf, solange das Gesetz es verlangt.', 'We keep invoices for as long as the law requires.')}
          </p>
          {mitNewsletter && (
            <div className="mb-3">
              <p className="m-0 text-[0.8125rem] leading-relaxed text-text-hell">
                {t('Dein Newsletter-Abo hängt nicht am Konto und bleibt bestehen, bis du dich abmeldest.', 'Your newsletter subscription is separate from your account and stays until you unsubscribe.')}
              </p>
              <label className="mt-1.5 flex items-start gap-2 text-[0.875rem] text-text">
                <input type="checkbox" checked={auchNewsletter} onChange={e => setAuchNewsletter(e.target.checked)} disabled={laedt} className="mt-1" />
                {t('Auch vom Newsletter abmelden', 'Also unsubscribe from the newsletter')}
              </label>
            </div>
          )}
          {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}
          <label className="block text-[0.8125rem] font-medium text-text">
            {t('Zur Bestätigung deine E-Mail-Adresse', 'Your email address, to confirm')}
            <input
              type="email" autoComplete="off" spellCheck={false} value={eingabe} onChange={e => setEingabe(e.target.value)} disabled={laedt}
              placeholder={email}
              className="mt-1.5 block h-11 w-full max-w-sm rounded-[0.625rem] border border-linie-stark bg-flaeche px-3 text-[1rem] text-tinte focus:border-marke focus:outline-none"
            />
          </label>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="submit" disabled={!passt || laedt}
              className="inline-flex min-h-10 cursor-pointer items-center rounded-full border-0 bg-schlecht px-4 text-[0.875rem] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-45"
            >
              {laedt ? t('Wird gelöscht …', 'Deleting …') : t('Konto endgültig löschen', 'Delete account permanently')}
            </button>
            <KleinerKnopf type="button" onClick={() => { setOffen(false); setEingabe(''); setFehler(''); }} disabled={laedt}>{t('Abbrechen', 'Cancel')}</KleinerKnopf>
          </div>
        </form>
      )}
    </Panel>
  );
}
